import React from 'react';
import { FieldValues } from 'react-hook-form';
import FormSuccess from '../../primitives/form-success';
import FormProvider from '../../primitives/form-provider';
import FadeTransition from '../../animations/transitions/fade-transition';
import { colors } from '../../primitives/colors';
import Typography from '../../primitives/typography';
import RadioGroup, { RadioItem } from '../../primitives/radio-group';
import Input from '../../primitives/input';
import StyledButton from '../../primitives/styled-button';
import { useMicrosite } from '../../../contexts/franchise/microsite-context';
import { breakpoints } from '../../primitives/tokens';
import Link from '../../primitives/link';

interface RoofingFormProps {
    submitButton?: string
    successMessage?: string
}

const RoofingForm = ({ submitButton, successMessage }: RoofingFormProps): JSX.Element => {
    const [showSuccess, setShowSuccess] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(false);
    const [isClaim, setIsClaim] = React.useState(false);

    const {
        franchise,
    } = useMicrosite();

    const successMessageRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        if (showSuccess && successMessageRef?.current) {
            setTimeout(() => {
                if (successMessageRef?.current) {
                    successMessageRef.current.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                        inline: 'nearest',
                    });
                }
            }, 500);
        }
    }, [showSuccess]);

    // NOTE: changes this to use <br /> over \n since the Contact Endpoint uses HTML to generate the content thus the \n wouldn't render correctly
    const constructCommentsField = (serviceType: string, insuranceCarrier: string | undefined | null): string | undefined | null => {
        let submissionPageComment = `Submitted via Roofing Page<br />Service Type: ${serviceType}`;
        if (insuranceCarrier) {
            submissionPageComment += `<br />Insurance Carrier: ${insuranceCarrier}`;
        }
        return submissionPageComment;
    };

    const onSubmit = (e: FieldValues): void => {
        const envelope = {
            addressLevel1: e['address-level1'],
            franchiseNumber: franchise?.franchiseNumber || 3000,
            emailAddress: e.email,
            firstName: e['first-name'],
            lastName: e['last-name'],
            phoneNumber: e.phone,
            comments: constructCommentsField(e['type-of-service'], e.carrier),
        };
        setIsLoading(true);
        fetch('/api/forms/franchise-contact', {
            method: 'POST',
            body: JSON.stringify(envelope),
            headers: {
                'Content-Type': 'application/json',
                'cache-control': 'no-cache',
            },
        }).then((res) => {
            setIsLoading(false);
            return res.json();
        }).then(() => {
            setShowSuccess(true);
            return true;
        }).catch(() => {
            setIsLoading(false);
            setShowSuccess(false);
            return false;
        });
    };

    return (
        <FadeTransition shouldChange={showSuccess.toString()}>
            {!showSuccess && (
                <FormProvider
                    onSubmit={onSubmit}
                    disabled={isLoading || showSuccess}
                    values={{
                        'type-of-service': 'replacement',
                    }}
                >
                    <div css={{
                        marginTop: '8px',
                        color: colors.shadesWhite,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        gap: '16px',
                        [breakpoints.tb]: {
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            rowGap: '0',
                        },
                    }}
                    >
                        <Typography
                            fontSize={{
                                dt: 'base',
                                mb: 'base',
                            }}
                            color="shadesWhite"
                        >
                            Type of Service:
                        </Typography>
                        <RadioGroup
                            name="type-of-service"
                            required
                            onChange={(e) => {
                                setIsClaim(e === 'insurance-claim');
                            }}
                        >
                            <RadioItem value="replacement">
                                Replacement
                            </RadioItem>
                            <RadioItem value="insurance-claim">
                                Insurance Claim
                            </RadioItem>
                        </RadioGroup>
                    </div>
                    <div css={{
                        display: 'flex',
                        flexFlow: 'wrap',
                        columnGap: '44px',
                        rowGap: '21px',
                        marginTop: '9px',
                        '> span': {
                            width: 'calc(50% - 22px)',
                            [breakpoints.tb]: {
                                width: '100%',
                            },
                        },
                        [breakpoints.tb]: {
                            rowGap: '12px',
                        },
                        'input:not(:placeholder-shown)': {
                            backgroundColor: colors.shadesParchment,
                        },
                    }}
                    >
                        <span>
                            <Input name="first-name" placeholder="First Name" required />
                        </span>
                        <span>
                            <Input name="last-name" placeholder="Last Name" required />
                        </span>
                        <span>
                            <Input name="email" placeholder="Email Address" autoComplete="email" required />
                        </span>
                        <span>
                            <Input name="phone" type="tel" placeholder="Phone Number" autoComplete="tel" />
                        </span>
                        {isClaim && (
                            <span css={{ width: '100%' }}>
                                <Input name="carrier" placeholder="Insurance Carrier" />
                            </span>
                        )}
                    </div>
                    <div css={{
                        marginTop: '21px',
                        display: 'flex',
                        gap: '24px',
                        [breakpoints.tb]: {
                            flexDirection: 'column',
                            marginTop: '12px',
                        },
                    }}
                    >
                        <StyledButton type="submit" loading={isLoading} insetColor={colors.shadesBlack}>
                            {submitButton || 'Submit'}
                        </StyledButton>
                        <Typography
                            color="shadesWhite"
                            fontSize={{
                                dt: 'sm',
                                tb: 'xs',
                            }}
                            css={{
                                maxWidth: '420px',
                                a: {
                                    color: colors.primaryOrange,
                                    textDecoration: 'underline',
                                    transition: 'color 300ms cubic-bezier(0.4, 0, 0.2, 1)',
                                    ':hover': {
                                        color: colors.primaryOrangeHover,
                                    },
                                },
                            }}
                        >

                            By submitting this form, you are expressly consenting to
                            receiving marketing and service-related calls or texts from
                            SERVPRO Industries, LLC  and its franchisees.
                            Consent is not a condition of purchase. Reply STOP to stop.
                            You are also expressly agreeing to our
                            {' '}
                            {' '}
                            <Link to="https://www.servpro.com/legal/terms" target="_blank">
                                Terms of Website Use
                            </Link>
                            {' '}
                            and
                            {' '}
                            <Link to="https://www.servpro.com/legal/privacy" target="_blank">
                                Privacy Policy
                            </Link>
                            .
                        </Typography>
                    </div>
                </FormProvider>
            )}
            <div ref={successMessageRef}>
                {showSuccess && (
                    <FormSuccess dangerouslySetInnerHTML={{ __html: successMessage || 'Submitted successfully.' }} />
                )}
            </div>
        </FadeTransition>
    );
};

export default RoofingForm;
